import { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Notifications from "./pages/Notifications";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Members from "./pages/Members";
import Events from "./pages/Events";
import Projects from "./pages/Projects";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import About from "./pages/About";
import NotFound from "./pages/NotFound";
import AdminRegister from "./pages/AdminRegister";
import Announcements from "./pages/Announcements";
import ContactUs from "./pages/ContactUs";
import Issuer from "./pages/Issuer";
import VerifyCertificate from "./pages/VerifyCertificate";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import EduResources from "./pages/EduResources";
import AdminResources from "./pages/AdminResources";


import AboutUs from "./pages/AboutUs";
import AchievementsAndBadges from "./pages/AchievementsAndBadges";
import AdminAnalytics from "./pages/AdminAnalytics";
import AlumniNetwork from "./pages/AlumniNetwork";
import BulkCertificate from "./pages/BulkCertificate";
import Challenge from "./pages/Challenge";
import CollaborationHub from "./pages/CollaborationHub";
import EventsAndGallery from "./pages/EventsAndGallery";
import HackathonWallOfFame from "./pages/HackathonWallOfFame";
import MyDashboard from "./pages/MyDashboard";
import ProfilePage from "./pages/ProfilePage";
import ProjectsShowcase from "./pages/ProjectsShowcase";
import SearchPage from "./pages/SearchPage";
import Gallery from "./pages/Gallery";




const queryClient = new QueryClient();
const API_BASE_URL = 'https://og-backend-mwwi.onrender.com/api';


interface SessionData {
  message: string;
  role?: string;
}

const ProtectedRoute = ({ children, requireAdmin = false }: { children: JSX.Element; requireAdmin?: boolean }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('ProtectedRoute: Checking session, cookies:', document.cookie);
        const response = await fetch(`${API_BASE_URL}/check-session-status`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        console.log('ProtectedRoute: Session check headers:', Object.fromEntries(response.headers));
        const data: SessionData = await response.json();
        console.log('ProtectedRoute: Session check response:', data);
        if (response.ok && data.message === 'Session active') {
          setIsAuthenticated(true);
          setRole(data.role || 'user');
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('ProtectedRoute: Session check error:', error);
        setIsAuthenticated(false);
      }
    };
    checkSession();
  }, []);

  if (isAuthenticated === null) {
    return <div className="text-white text-center min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to={requireAdmin ? '/admin-login' : '/login'} replace />;
  }

  if (requireAdmin && role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin-register" element={<AdminRegister />} />
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/contactus" element={<ContactUs />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/verify-certificate" element={<VerifyCertificate />} />
          <Route path="/edu-resources" element={<EduResources />} />


          <Route path="/aboutus" element={<AboutUs />} />
          <Route path="/achievements" element={<AchievementsAndBadges />} />
          <Route path="/admin-analytics" element={<AdminAnalytics />} />
          <Route path="/alumni" element={<AlumniNetwork />} />
          <Route path="/bulk-certificate" element={<BulkCertificate />} />
          <Route path="/challenge" element={<Challenge />} />
          <Route path="/collaboration" element={<CollaborationHub />} />
          <Route path="/eventsandgallery" element={<EventsAndGallery />} />
          <Route path="/hackathon-wall" element={<HackathonWallOfFame />} />
          <Route path="/mydashboard" element={<MyDashboard />} /> 
          <Route path="/profilepage" element={<ProfilePage />} />
          <Route path="/projectsshowcase" element={<ProjectsShowcase />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/gallery" element={<Gallery />} />
         


          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/members"
            element={
              <ProtectedRoute>
                <Members />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:id"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/issuer"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Issuer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-resources"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminResources />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
